import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Gauge,
  Lock,
  Printer,
  Receipt,
  RefreshCw,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { mostrarPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { formatNumber } from '@/lib/utils';

import {
  turnoApi,
  useCambiarOperacionBomba,
  useEstadoBombasTurno,
  useMutarTurno,
  useTurnoActual,
} from '../turno.api';
import type { ContometroPtsDelTurno, EstadoBombaDelTurno } from '../turno.types';

/** Mensaje legible de un error de la API. */
function mensajeError(error: unknown, porDefecto: string): string {
  return error instanceof ApiError ? error.message : porDefecto;
}

/** Etiqueta del estado que comunica el controlador para un lado. */
function etiquetaEstado(paquete: EstadoBombaDelTurno | undefined): {
  texto: string;
  variante: 'success' | 'warning' | 'danger' | 'neutral';
} {
  switch (paquete?.Type) {
    case 'PumpFillingStatus':
      return { texto: 'Despachando', variante: 'success' };
    case 'PumpEndOfTransactionStatus':
      return { texto: 'Despacho terminado', variante: 'warning' };
    case 'PumpIdleStatus':
      return { texto: 'Disponible', variante: 'neutral' };
    case 'PumpOfflineStatus':
      return { texto: 'Sin conexión', variante: 'danger' };
    default:
      return { texto: 'Sin respuesta', variante: 'warning' };
  }
}

/** «la bomba 2», «las bombas 2 y 3» o «las bombas 2, 3 y 4». */
function listarBombas(bombas: number[]): string {
  const etiquetas = bombas.map((bomba) => `bomba ${bomba}`);

  if (etiquetas.length <= 1) return etiquetas[0] ?? '';

  return `${etiquetas.slice(0, -1).join(', ')} y ${etiquetas[etiquetas.length - 1]}`;
}

/**
 * Cerrar turno (`/turno/cerrar`).
 *
 * Puerto de `Turno::culminar_turno` + `views/menu/turno/culminar_turno.php` del panel
 * anterior: la pantalla propia del cierre, con el contómetro de cada lado
 * («anterior», «actual» y «diferencia»), el aviso de los contadores con diferencia
 * negativa (que convierte el cierre en **forzado**) y el botón que suspende las
 * bombas y vuelve a leer los contómetros antes de confirmar.
 *
 * El cierre guarda el efectivo entregado, la lectura final de cada manguera
 * (`POST /api/turno/cerrar`) y libera los surtidores del operador; después ofrece su
 * **PDF de cierre**, el `TICKET CIERRE TURNO` de 80 mm (`GET /api/turno/ticket`, el
 * mismo cuadre del reporte de turnos).
 */
export function TurnoCerrarPage() {
  useDocumentTitle('Turno · Cerrar turno');

  const navegar = useNavigate();
  const consulta = useTurnoActual();
  const estadoBombas = useEstadoBombasTurno();
  const cambiarOperacionBomba = useCambiarOperacionBomba();
  const mutaciones = useMutarTurno();

  const [confirmarProceso, setConfirmarProceso] = useState(false);
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [contometrosProcesados, setContometrosProcesados] = useState(false);
  const [errorPdf, setErrorPdf] = useState('');
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [cerrado, setCerrado] = useState<{
    turnoId: number;
    transacciones: number;
    boletas: number;
  } | null>(null);

  const datos = consulta.data;
  const turno = datos?.turno ?? null;
  /**
   * Ventas del turno que todavía no tienen comprobante. El cierre las boletea solo y no deja
   * cerrar mientras quede alguna: un turno no se cierra con transacciones libres.
   */
  const libres = datos?.transacciones_libres ?? 0;
  /**
   * Cierre a ciegas (`config.closure`): el operador no ve ninguna cifra del turno —los
   * contadores van en `*`— para que no pueda calcular la venta antes de entregar el efectivo.
   * La API tampoco manda los números, así que aquí sólo se deja de pintarlos.
   */
  const aCiegas = Boolean(datos?.cierre_a_ciegas);
  /**
   * Mangueras que forzarán el cierre. Se toma de `diferencias_negativas` (que la API calcula
   * **sin** el volumen cuando el cierre es a ciegas) y no de `volumen_turno`, que en ese caso
   * llega en `null`.
   */
  const diferenciasNegativas = datos?.diferencias_negativas ?? [];
  const cierreForzado = diferenciasNegativas.length > 0;

  /** Contómetros agrupados por lado (bomba), como la tabla del panel anterior. */
  const lados = useMemo(() => {
    const mapa = new Map<number, ContometroPtsDelTurno[]>();

    for (const lectura of datos?.contometros_pts ?? []) {
      const filas = mapa.get(lectura.bomba) ?? [];
      filas.push(lectura);
      mapa.set(lectura.bomba, filas);
    }

    return [...mapa.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([bomba, filas]) => ({ bomba, filas }));
  }, [datos]);

  /**
   * El cierre está bloqueado hasta procesar los contómetros (como el panel anterior,
   * donde el botón arranca deshabilitado). Si el operador no tiene mangueras asignadas
   * no hay nada que procesar y se puede cerrar directamente.
   */
  const hayContometros = lados.length > 0;
  const puedeCerrar = !hayContometros || contometrosProcesados;

  /** Cifra de un contómetro: en **cierre a ciegas** siempre es `*`. */
  const cifra = (valor: number | null) =>
    aCiegas ? '*' : valor === null ? 'Sin lectura' : formatNumber(valor);
  /** Diferencia (volumen) del turno: `*` en cierre a ciegas. */
  const cifraDiferencia = (valor: number | null) =>
    aCiegas ? '*' : valor === null ? '—' : formatNumber(valor);
  /** ¿La manguera forzará el cierre? (la API lo dice sin el volumen cuando es a ciegas). */
  const esNegativa = (bomba: number, manguera: number) =>
    diferenciasNegativas.some((item) => item.bomba === bomba && item.manguera === manguera);

  const estadoDe = (bomba: number) =>
    estadoBombas.data?.packets.find((paquete) => Number(paquete.Data?.Pump) === bomba);

  const abrirPdfCierre = async (turnoId: number) => {
    setErrorPdf('');

    try {
      await mostrarPdfAutenticado(turnoApi.ticket(turnoId));
    } catch (error) {
      setErrorPdf(error instanceof Error ? error.message : 'No se pudo abrir el PDF de cierre.');
    }
  };

  /**
   * «Procesar contometros» del panel anterior: espera a que no haya cargas en curso,
   * suspende las bombas asignadas y vuelve a leer los contómetros del PTS.
   */
  const procesarContometros = async () => {
    setAviso(null);
    setProcesando(true);

    try {
      const estado = await estadoBombas.refetch();
      const paquetes = estado.data?.packets ?? [];
      const despachando = paquetes
        .filter((paquete) => paquete.Type === 'PumpFillingStatus')
        .map((paquete) => Number(paquete.Data?.Pump))
        .filter((bomba) => Number.isFinite(bomba));

      if (despachando.length > 0) {
        setAviso({
          tono: 'error',
          texto: `Hay cargas en curso en ${listarBombas(despachando)}. Espera a que terminen para procesar los contómetros.`,
        });
        return;
      }

      const asignadas = estado.data?.lados ?? [];
      const porSuspender = asignadas.filter(
        (bomba) => estado.data?.operaciones?.[bomba] !== 'Off',
      );

      for (const bomba of porSuspender) {
        await cambiarOperacionBomba.mutateAsync({ bomba, estado: 'Off' });
      }

      await consulta.refetch();
      setContometrosProcesados(true);
      setAviso({
        tono: 'success',
        texto:
          porSuspender.length === 0
            ? 'Contómetros actualizados: tus bombas ya estaban suspendidas.'
            : `Contómetros actualizados. Se suspendieron ${porSuspender.length} ${porSuspender.length === 1 ? 'bomba' : 'bombas'}.`,
      });
    } catch (error) {
      setAviso({
        tono: 'error',
        texto: mensajeError(error, 'No se pudieron procesar los contómetros.'),
      });
    } finally {
      setProcesando(false);
      setConfirmarProceso(false);
    }
  };

  const cerrarTurno = () => {
    setAviso(null);

    // El panel anterior también cierra sin pedir el efectivo (`total_entregado = 0`).
    mutaciones.cerrar.mutate(
      { total_entregado: 0 },
      {
        onSuccess: (respuesta) => {
          setConfirmarCierre(false);
          setCerrado({
            turnoId: respuesta.turno_id,
            transacciones: respuesta.transacciones,
            boletas: respuesta.boletas_generadas ?? 0,
          });
        },
        onError: (error) => {
          setConfirmarCierre(false);
          setAviso({ tono: 'error', texto: mensajeError(error, 'No se pudo cerrar el turno.') });
          // Si el cierre se rechazó por ventas sin comprobante, se refresca el conteo; y si
          // fue por una bomba despachando, el estado en vivo de los surtidores.
          void consulta.refetch();
          void estadoBombas.refetch();
        },
      },
    );
  };

  /**
   * Bolet ea las transacciones libres sin cerrar el turno. El cierre ya lo intenta por su
   * cuenta, así que esto es el reintento: cuando el servicio de boleteo tarda más de lo que
   * aguanta el cierre, el operador lo lanza desde aquí y vuelve a intentar cerrar.
   */
  const boletearLibres = () => {
    setAviso(null);

    mutaciones.boletear.mutate(undefined, {
      onSuccess: (respuesta) => {
        setAviso({ tono: 'success', texto: respuesta.mensaje });
        void consulta.refetch();
      },
      onError: (error) => {
        setAviso({
          tono: 'error',
          texto: mensajeError(error, 'No se pudieron emitir los comprobantes del turno.'),
        });
        void consulta.refetch();
      },
    });
  };

  const volverAlTurno = () => navegar('/turno');

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Operador</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Lock className="size-6 text-brand-500" aria-hidden="true" />
            Cerrar turno
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-500">
            Revisa los contómetros de tus lados, procesa las lecturas del controlador y confirma el
            cierre. El PDF de cierre se abre al terminar.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            leftIcon={<ArrowLeft className="size-4" aria-hidden="true" />}
            onClick={volverAlTurno}
          >
            Volver a mi turno
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert
          variant={aviso.tono}
          title={aviso.tono === 'success' ? 'Listo' : 'No se pudo completar'}
        >
          {aviso.texto}
        </Alert>
      ) : null}

      {consulta.isLoading ? (
        <SectionCard title="Cierre del turno">
          <Skeleton className="h-40 w-full" />
        </SectionCard>
      ) : null}

      {consulta.isError ? (
        <SectionCard title="Cierre del turno">
          <Alert variant="error" title="No se pudo leer tu turno">
            {mensajeError(consulta.error, 'Vuelve a intentarlo en unos segundos.')}
          </Alert>
        </SectionCard>
      ) : null}

      {datos && !datos.abierto ? (
        <SectionCard
          title="No tienes un turno abierto"
          description="Sólo puedes cerrar el turno que tienes abierto ahora mismo."
        >
          <Button onClick={volverAlTurno}>Ir a mi turno</Button>
        </SectionCard>
      ) : null}

      {turno ? (
        <SectionCard
          title={`Contómetros del turno #${turno.id}`}
          description={
            aCiegas
              ? 'Cierre a ciegas: se procesan las lecturas de tus mangueras, pero no se muestran las cifras.'
              : 'Cada fila es una manguera de tus lados: la lectura con la que abriste, la última del controlador y su diferencia.'
          }
          action={
            <Button
              variant="outline"
              size="sm"
              loading={consulta.isFetching}
              onClick={() => void consulta.refetch()}
            >
              <RefreshCw className="mr-1 size-3.5" aria-hidden="true" />
              Actualizar lecturas
            </Button>
          }
        >
          {aCiegas ? (
            <Alert variant="info" title="Cierre a ciegas" className="mb-4">
              El efectivo esperado no se muestra: lo calcula el supervisor al verificar el turno.
              Procesa los contómetros y confirma el cierre como siempre; las cifras las revisa él.
            </Alert>
          ) : null}
          {lados.length === 0 ? (
            <Alert variant="warning" title="No hay contómetros que revisar">
              Tu usuario no tiene surtidores asignados en el PTS, así que no hay lecturas que
              procesar antes del cierre.
            </Alert>
          ) : (
            <div className="space-y-4">
              {lados.map((lado) => {
                const paquete = estadoDe(lado.bomba);
                const estado = etiquetaEstado(paquete);
                const operacion = estadoBombas.data?.operaciones?.[lado.bomba];

                return (
                  <div
                    key={lado.bomba}
                    className="overflow-hidden rounded-control border border-ink-100"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-ink-50 px-3 py-2">
                      <span className="text-sm font-semibold text-ink-800">
                        Lado {lado.bomba}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={estado.variante}>{estado.texto}</Badge>
                        <Badge
                          variant={
                            operacion === 'Off' ? 'danger' : operacion === 'On' ? 'success' : 'warning'
                          }
                        >
                          {operacion === 'Off'
                            ? 'Suspendida'
                            : operacion === 'On'
                              ? 'Activa'
                              : 'Control no disponible'}
                        </Badge>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-xs uppercase tracking-wide text-ink-500">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold">Manguera</th>
                            <th className="px-3 py-2 text-left font-semibold">Producto</th>
                            <th className="px-3 py-2 text-right font-semibold">Contador anterior</th>
                            <th className="px-3 py-2 text-right font-semibold">Contador actual</th>
                            <th className="px-3 py-2 text-right font-semibold">Diferencia</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lado.filas.map((lectura) => {
                            const negativa = esNegativa(lectura.bomba, lectura.manguera);

                            return (
                              <tr
                                key={`${lectura.bomba}:${lectura.manguera}`}
                                className={negativa ? 'border-t border-ink-100 bg-rose-50' : 'border-t border-ink-100'}
                              >
                                <td className="px-3 py-2 text-ink-700">
                                  Manguera {lectura.manguera}
                                </td>
                                <td className="px-3 py-2 text-ink-600">
                                  {lectura.producto ?? 'Sin producto'}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-ink-600">
                                  {cifra(lectura.contometro_llegada)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-ink-600">
                                  {cifra(lectura.contometro_actual)}
                                </td>
                                <td
                                  className={
                                    negativa
                                      ? 'px-3 py-2 text-right font-semibold tabular-nums text-rose-700'
                                      : 'px-3 py-2 text-right tabular-nums text-ink-800'
                                  }
                                >
                                  {cifraDiferencia(lectura.volumen_turno)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      ) : null}

      {turno ? (
        <SectionCard
          title="Confirmar el cierre"
          description="Procesa los contómetros (suspende tus bombas y vuelve a leerlas) y confirma el cierre del turno."
        >
          <div className="space-y-4">
            {libres > 0 ? (
              <Alert variant="warning" title="Ventas sin comprobante">
                Tu turno tiene <strong>{libres}</strong> transacción(es) del surtidor sin
                comprobante. Al cerrar se <strong>boletan automáticamente</strong>: un turno no se
                cierra con ventas sin documento. Si el boleteo se demora, usa el botón para
                lanzarlo y vuelve a intentar el cierre.
              </Alert>
            ) : null}

            {cierreForzado ? (
              <Alert variant="warning" title="Contadores con diferencia negativa">
                Las siguientes mangueras tienen una lectura final menor a la inicial. Si continúas,
                el cierre será <strong>forzado</strong> y los contadores se actualizarán con la
                lectura recibida.
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {diferenciasNegativas.map((lectura) => (
                    <li key={`${lectura.bomba}:${lectura.manguera}`}>
                      Lado {lectura.bomba} · manguera {lectura.manguera} (
                      {lectura.producto ?? 'sin producto'}):{' '}
                      {lectura.volumen_turno === null ? '*' : formatNumber(lectura.volumen_turno)}
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                leftIcon={<Gauge className="size-4" aria-hidden="true" />}
                loading={procesando}
                disabled={!hayContometros || mutaciones.cerrar.isPending}
                onClick={() => setConfirmarProceso(true)}
              >
                Procesar contómetros
              </Button>
              {libres > 0 ? (
                <Button
                  variant="outline"
                  leftIcon={<Receipt className="size-4" aria-hidden="true" />}
                  loading={mutaciones.boletear.isPending}
                  disabled={mutaciones.cerrar.isPending}
                  onClick={boletearLibres}
                >
                  Boletear {libres} venta{libres === 1 ? '' : 's'} sin comprobante
                </Button>
              ) : null}
              <Button
                variant="danger"
                leftIcon={
                  cierreForzado ? (
                    <AlertTriangle className="size-4" aria-hidden="true" />
                  ) : (
                    <Lock className="size-4" aria-hidden="true" />
                  )
                }
                loading={mutaciones.cerrar.isPending}
                disabled={procesando || !puedeCerrar}
                onClick={() => setConfirmarCierre(true)}
              >
                {cierreForzado ? 'Forzar cierre de turno' : 'Cerrar turno'}
              </Button>
            </div>

            {puedeCerrar ? null : (
              <p className="flex items-start gap-2 text-xs leading-relaxed text-amber-700">
                <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span>
                  Primero pulsa <strong>Procesar contómetros</strong>: es obligatorio dejar las
                  bombas suspendidas y las lecturas al día antes de cerrar el turno.
                </span>
              </p>
            )}
          </div>
        </SectionCard>
      ) : null}

      <ConfirmDialog
        open={confirmarProceso}
        title="Procesar contómetros"
        description="Se suspenderán tus bombas asignadas (no aceptarán nuevos despachos) y se volverán a leer los contómetros del controlador. No cierra el turno."
        confirmLabel="Procesar contómetros"
        tone="primary"
        loading={procesando}
        onClose={() => setConfirmarProceso(false)}
        onConfirm={() => void procesarContometros()}
      />

      <ConfirmDialog
        open={confirmarCierre}
        title={cierreForzado ? 'Cierre forzado del turno' : 'Cerrar turno'}
        description={
          cierreForzado
            ? `Hay ${diferenciasNegativas.length} contador(es) con diferencia negativa: se forzará el cierre y se actualizarán con la lectura recibida. No se puede deshacer.`
            : libres > 0
              ? `Se boletarán ${libres} transacción(es) sin comprobante y se cerrará el turno. Después no podrás registrar más gastos ni bóvedas en él.`
              : 'Se cierra el turno y se guarda la lectura final de tus mangueras. Después no podrás registrar más gastos ni bóvedas en él.'
        }
        confirmLabel={cierreForzado ? 'Confirmar cierre forzado' : 'Cerrar turno'}
        tone="danger"
        loading={mutaciones.cerrar.isPending}
        onClose={() => setConfirmarCierre(false)}
        onConfirm={cerrarTurno}
      />

      <Dialog
        open={cerrado !== null}
        onClose={volverAlTurno}
        title="Turno cerrado"
        description={cerrado ? `Turno #${cerrado.turnoId}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={volverAlTurno}>
              Volver a mi turno
            </Button>
            {/* Con cierre a ciegas el PDF del cuadre no se entrega al operador (la API
                también lo rechaza): con el faltante en la mano podría taparlo con un gasto
                o una bóveda inventada. */}
            {aCiegas ? null : (
              <Button
                leftIcon={<Printer className="size-4" aria-hidden="true" />}
                onClick={() => cerrado && void abrirPdfCierre(cerrado.turnoId)}
              >
                Abrir PDF de cierre
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-3">
          {errorPdf ? (
            <Alert variant="error" title="No se pudo abrir el PDF de cierre">
              {errorPdf}
            </Alert>
          ) : null}
          <p className="flex items-start gap-2 text-sm text-ink-700">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" aria-hidden="true" />
            <span>
              Se guardaron {cerrado?.transacciones ?? 0} transacciones del surtidor y la lectura
              final de tus mangueras.
            </span>
          </p>
          {cerrado && cerrado.boletas > 0 ? (
            <p className="flex items-start gap-2 text-sm text-ink-700">
              <Receipt className="mt-0.5 size-4 shrink-0 text-emerald-600" aria-hidden="true" />
              <span>
                El boleteo automático emitió <strong>{cerrado.boletas}</strong> comprobante(s) de
                las ventas que no tenían documento.
              </span>
            </p>
          ) : null}
          {aCiegas ? (
            <p className="text-xs leading-relaxed text-ink-500">
              El cuadre del turno es <strong>a ciegas</strong>: los importes los revisa el
              supervisor al verificar el turno, junto con el efectivo que entregues.
            </p>
          ) : (
            <p className="text-xs leading-relaxed text-ink-500">
              El PDF de cierre es el mismo cuadre que revisa el supervisor en el reporte de
              turnos.
            </p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
